// React
import { useEffect, useState } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp, faClipboard, faClipboardCheck } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { getDataTestId } from "../functions/data-test-ids";
import { isNullOrUndefined } from "../functions/general";
import { getDecimalMark, parseDecimal } from "../functions/locales";
import { InputCopyButton } from "./input-clipboard-copy-button";
import { InputErrorIndicator } from "./input-error-indicator";
import { InputIcon } from "./input-icon";
import { getInputClassDetails } from "./input-shared.jsx";

// CSS
import "./inputs.css";

function InputNumberArrow(props) {
    if (!props.Visible) {
        return null;
    }

    const getIcon = () => {
        return props.Direction === "up" ? faAngleUp : faAngleDown;
    };

    return (
        <button type="button" className="eone-ui-input-number-arrow" onClick={props.OnClick} title={props.Title} data-testid={getDataTestId(props.DataTestId, props.Direction)}>
            <FontAwesomeIcon fixedWidth={true} icon={getIcon()} />
        </button>
    )
}

function InputNumber(props) {
    const [errorMessage, setErrorMessage] = useState("");
    const [copied, setCopied] = useState(false);
    const [focused, setFocused] = useState(false);
    const [decimalMark, setDecimalMark] = useState(null);

    useEffect(() => {
        setDecimalMark(getDecimalMark(props.Locale));
    }, [props.Locale]);

    const changeValue = (value) => {
        if (typeof props.OnChange === "function") {
            props.OnChange(value);
        }

        if (typeof props.OnError === "function") {
            const hasErrors = checkForErrors(value);
            props.OnError(hasErrors);
        }
    };

    const changeHandler = (e) => {
        const value = e.target.value;
        const numberValue = props.HasDecimals ? parseDecimal(value, props.Locale, props.DecimalPlaces) : parseInt(value);
        if (numberValue === null || numberValue === undefined || isNaN(numberValue)) {
            if (!isNullOrUndefined(props.MinimumValue)) changeValue(props.MinimumValue);
            else changeValue(0);
        } else {
            if (!isNullOrUndefined(props.MinimumValue) && numberValue < props.MinimumValue) return;
            if (!isNullOrUndefined(props.MaximumValue) && numberValue > props.MaximumValue) return;
            changeValue(e.target.value);
        }
    };

    const updateValue = (value) => {
        const hasErrors = checkForErrors(value);
        if (!hasErrors) {
            if (typeof props.OnBlur === "function") {
                props.OnBlur(value);
            }
        }
    };

    const blurHandler = (e) => {
        setFocused(false);
        let value = e.target.value;
        if (!value) return;
        value = value.toString().replace(/^0+/, "");
        const numberValue = props.HasDecimals ? parseDecimal(value, props.Locale, props.DecimalPlaces) : parseInt(value);

        if (numberValue === null || numberValue === undefined || isNaN(numberValue)) updateValue(0);
        else if (!isNullOrUndefined(props.MinimumValue) && numberValue < props.MinimumValue) updateValue(props.MinimumValue);
        else if (!isNullOrUndefined(props.MaximumValue) && numberValue > props.MaximumValue) updateValue(props.MaximumValue);
        else updateValue(numberValue);
    };

    const focusHandler = (e) => {
        setFocused(true);
        const temp_value = e.target.value;
        e.target.value = "";
        e.target.value = temp_value;
        if (typeof props.OnFocus === "function") {
            props.OnFocus();
        }
    };

    const incrementValue = () => {
        let numberValue = parseFloat(props.Value);
        if (numberValue === null || numberValue === undefined || isNaN(numberValue)) numberValue = 0;
        numberValue = numberValue + 1;
        if (!isNullOrUndefined(props.MaximumValue) && numberValue > props.MaximumValue) return;
        changeValue(numberValue);
        updateValue(numberValue);
    };

    const decrementValue = () => {
        let numberValue = parseFloat(props.Value);
        if (numberValue === null || numberValue === undefined || isNaN(numberValue)) numberValue = 0;
        numberValue = numberValue - 1;
        if (!isNullOrUndefined(props.MinimumValue) && numberValue < props.MinimumValue) return;
        changeValue(numberValue);
        updateValue(numberValue);
    };

    const filterInvalidChars = (e) => {
        switch (e.key) {
            case "ArrowUp":
                incrementValue();
                e.preventDefault();
                return;
            case "ArrowDown":
                decrementValue();
                e.preventDefault();
                return;
            case "ArrowRight":
            case "ArrowLeft":
            case "Backspace":
            case "Delete":
            case "Tab":
                return true;
            case "-":
                return props.AllowNegative;
        }
        if (e.ctrlKey && e.key === "v") {
            return true;
        }
        let validChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
        if (decimalMark && props.DecimalPlaces) {
            validChars.push(decimalMark);
        }
        if (!validChars.includes(e.key)) {
            e.preventDefault();
        }
    };

    const copyToClipboard = async () => {
        setCopied(true);
        setTimeout(clearCopiedState, 5000);
        await navigator.clipboard.writeText(props.Value);
    };

    const clearCopiedState = () => {
        setCopied(false);
    };

    const getClipboardIcon = () => {
        return copied ? faClipboardCheck : faClipboard;
    };

    const checkForErrors = (value) => {
        if (props.Required !== undefined && props.Required && value === "") {
            setErrorMessage("required");
            return true;
        }
        setErrorMessage("");
        return false;
    };

    const getClassName = () => {
        let className = "eone-ui-input";
        if (props.ReadOnly) {
            if (props.HasDecimals) {
                className = className + " eone-ui-input-decimal-readonly";
            } else {
                className = className + " eone-ui-input-integer-readonly";
            }
        } else {
            if (props.HasDecimals) {
                className = className + " eone-ui-input-decimal";
            } else {
                className = className + " eone-ui-input-integer";
            }
        }
        if (props.Class) {
            className = className + " " + props.Class;
        }
        return className;
    };

    const getAutocomplete = () => {
        return props.AutoComplete ? "on" : "off";
    };

    const getContainerClass = () => {
        const classDetails = getInputClassDetails(props.Value, errorMessage);
        if (props.ContainerClass) {
            return props.ContainerClass + ' ' + classDetails;
        }
        if (focused) {
            return "eone-ui-input-container eone-ui-input-container-focused " + classDetails;
        }
        return "eone-ui-input-container " + classDetails;
    };

    const showUpDownButtons = props.ReadOnly !== true && !props.HasDecimals;
    
    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if(props.ArrowsAtEnd) {
        return (
            <div className={getContainerClass()} >
                <InputIcon Icon={props.Icon} DataTestId={props.DataTestId} />
                <input
                    autoFocus={props.AutoFocus}
                    autoComplete={getAutocomplete()}
                    className={getClassName()}
                    id={props.Id}
                    maxLength={props.MaxLength}
                    onBlur={blurHandler}
                    onChange={changeHandler}
                    onFocus={focusHandler}
                    readOnly={props.ReadOnly}
                    onKeyDown={filterInvalidChars}
                    placeholder={props.Placeholder}
                    type="text"
                    style={{ ...props.Style, textAlign: 'start' }}
                    title={props.Title || props.Value}
                    value={props.Value}
                    data-testid={props.DataTestId} />
                <InputNumberArrow Visible={showUpDownButtons} DataTestId={props.DataTestId} Direction="up" Title="Increment value" OnClick={incrementValue} />
                <div style={{ height: '15px', width: 1, backgroundColor: 'gray'  }} />
                <InputNumberArrow Visible={showUpDownButtons} DataTestId={props.DataTestId} Direction="down" Title="Decrement value" OnClick={decrementValue} />
                <InputCopyButton
                    Show={props.ShowCopyButton}
                    Icon={getClipboardIcon()}
                    Copy={copyToClipboard}
                    DataTestId={props.DataTestId} />
                <InputErrorIndicator
                    Error={errorMessage}
                    DataTestId={props.DataTestId} />
            </div>
        );
    }

    return (
        <div className={getContainerClass()} >
            <InputNumberArrow Visible={showUpDownButtons} DataTestId={props.DataTestId} Direction="down" Title="Decrement value" OnClick={decrementValue} />
            <InputIcon Icon={props.Icon} DataTestId={props.DataTestId} />
            <input
                autoFocus={props.AutoFocus}
                autoComplete={getAutocomplete()}
                className={getClassName()}
                id={props.Id}
                maxLength={props.MaxLength}
                onBlur={blurHandler}
                onChange={changeHandler}
                onFocus={focusHandler}
                readOnly={props.ReadOnly}
                onKeyDown={filterInvalidChars}
                placeholder={props.Placeholder}
                type="text"
                style={props.Style}
                title={props.Title || props.Value}
                value={props.Value}
                data-testid={props.DataTestId} />
            <InputCopyButton
                Show={props.ShowCopyButton}
                Icon={getClipboardIcon()}
                Copy={copyToClipboard}
                DataTestId={props.DataTestId} />
            <InputErrorIndicator
                Error={errorMessage}
                DataTestId={props.DataTestId} />
            <InputNumberArrow Visible={showUpDownButtons} DataTestId={props.DataTestId} Direction="up" Title="Increment value" OnClick={incrementValue} />
        </div>
    );
}


// function InputNumber2(props) {
//     const [errorMessage, setErrorMessage] = useState("");
//     const [copied, setCopied] = useState(false);
//     const [focused, setFocused] = useState(false);
//     const [decimalMark, setDecimalMark] = useState(".");

//     useEffect(() => {
//         setDecimalMark(getDecimalMark(props.Locale));
//     }, [props.Locale]);

//     const stringToNullableNumber = (stringValue) => {
//         if (stringValue === null || stringValue === undefined) {
//             // definitely not a number
//             return null;
//         }
//         // remove writespace
//         stringValue = stringValue.toString().trim();

//         if (/^0+$/.test(stringValue)) {
//             // if the string is all 0's then it's just a zero
//             stringValue = "0";
//         } else {
//             // otherwise remove leading zeros
//             stringValue = stringValue.replace(/^0+/, "");
//         }

//         let nullableNumber = null;
//         if (props.HasDecimals) {
//             nullableNumber = parseFloat(stringValue);
//         } else {
//             nullableNumber = parseInt(stringValue);
//         }
//         if (isNaN(nullableNumber)) {
//             return null;
//         }
//         return nullableNumber;
//     }

//     const numberToString = (value) => {
//         if (value === null) {
//             return "";
//         }
//         return props.HasDecimals ? parseDecimal(value, props.Locale, props.DecimalPlaces).toString() : value.toString();
//     }

//     const clampNumber = (numberValue) => {
//         if (numberValue === null) {
//             return null;
//         }
//         if (props.MinimumValue !== undefined && numberValue < props.MinimumValue) {
//             return props.MinimumValue;
//         }
//         if (props.MaximumValue !== undefined && numberValue > props.MaximumValue) {
//             return props.MaximumValue;
//         }
//         return numberValue;
//     }

//     const onChange = (value) => {
//         if (typeof props.OnChange === "function") {
//             props.OnChange(value);
//         }
//     };

//     const changeHandler = (e) => {
//         const value = e.target.value;
//         if (typeof props.OnError === "function") {
//             const hasErrors = checkForErrors(value);
//             props.OnError(hasErrors);
//         }
//         const numberValue = stringToNullableNumber(value);
//         const stringValue = numberToString(numberValue);
//         onChange(stringValue);
//     };

//     const onUpdate = (value) => {
//         if (typeof props.OnBlur === "function") {
//             props.OnBlur(value);
//         }
//     };

//     const blurHandler = (e) => {
//         const value = e.target.value;
//         const hasErrors = checkForErrors(value);
//         let numberValue = clampNumber(stringToNullableNumber(value) ?? 0);
//         const stringValue = numberToString(numberValue);
//         if (!hasErrors) {
//             onUpdate(stringValue);
//         }
//     };

//     const focusHandler = (e) => {
//         setFocused(true);
//         const temp_value = e.target.value;
//         e.target.value = "";
//         e.target.value = temp_value;
//         if (typeof props.OnFocus === "function") {
//             props.OnFocus();
//         }
//     };

//     const incrementValue = () => {
//         const numberValue = stringToNullableNumber(props.Value) ?? 0;
//         const incrementedValue = clampNumber(numberValue + 1);
//         const stringValue = numberToString(incrementedValue)
//         onChange(stringValue);
//         onUpdate(stringValue);
//     };

//     const decrementValue = () => {
//         const numberValue = stringToNullableNumber(props.Value) ?? 0;
//         const decrementedValue = clampNumber(numberValue - 1);
//         const stringValue = numberToString(decrementedValue)
//         onChange(stringValue);
//         onUpdate(stringValue);
//     };


//     const filterInvalidChars = (e) => {
//         switch (e.key) {
//             case "ArrowUp":
//                 incrementValue();
//                 e.preventDefault();
//                 return;
//             case "ArrowDown":
//                 decrementValue();
//                 e.preventDefault();
//                 return;
//             case "ArrowRight":
//             case "ArrowLeft":
//             case "Backspace":
//             case "Delete":
//             case "Tab":
//                 return true;
//             case "-":
//                 return props.AllowNegative;
//         }
//         if (e.ctrlKey && e.key === "v") {
//             return true;
//         }
//         let validChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
//         if (decimalMark && props.DecimalPlaces) {
//             validChars.push(decimalMark);
//         }
//         if (!validChars.includes(e.key)) {
//             e.preventDefault();
//         }
//     };

//     const copyToClipboard = async () => {
//         setCopied(true);
//         setTimeout(clearCopiedState, 5000);
//         await navigator.clipboard.writeText(props.Value || "");
//     };

//     const clearCopiedState = () => {
//         setCopied(false);
//     };

//     const getClipboardIcon = () => {
//         return copied ? faClipboardCheck : faClipboard;
//     };

//     const checkForErrors = (value) => {
//         if (props.Required !== undefined && props.Required && value === "") {
//             setErrorMessage("required");
//             return true;
//         }
//         setErrorMessage("");
//         return false;
//     };

//     const getErrorMessage = (type) => {
//         switch (type) {
//             case "required":
//                 if (props.RequiredError !== undefined && props.RequiredError !== null) {
//                     return props.RequiredError;
//                 }
//                 return props.Messages?.RequiredField || "This field is required";
//             case "invalid":
//                 if (props.InvalidValues !== undefined && props.InvalidError !== null) {
//                     return props.InvalidError;
//                 }
//                 return props.Messages?.InvalidValue || "Value is invalid";
//             default:
//                 return null;
//         }
//     };

//     const getClassName = () => {
//         let className = "eone-ui-input";
//         if (props.ReadOnly) {
//             if (props.HasDecimals) {
//                 className = className + " eone-ui-input-decimal-readonly";
//             } else {
//                 className = className + " eone-ui-input-integer-readonly";
//             }
//         } else {
//             if (props.HasDecimals) {
//                 className = className + " eone-ui-input-decimal";
//             } else {
//                 className = className + " eone-ui-input-integer";
//             }
//         }
//         if (props.Class) {
//             className = className + " " + props.Class;
//         }
//         return className;
//     };

//     const getAutocomplete = () => {
//         return props.AutoComplete ? "on" : "off";
//     };

//     const getContainerClass = () => {
//         if (props.ContainerClass) {
//             return props.ContainerClass;
//         }
//         if (focused) {
//             return "eone-ui-input-container eone-ui-input-container-focused";
//         }
//         return "eone-ui-input-container";
//     };

//     if (props.Visible !== undefined && !props.Visible) {
//         return null;
//     }

//     const showUpDownButtons = props.ReadOnly !== true && !props.HasDecimals;
//     return (
//         <div className={getContainerClass()} >
//             <InputNumberArrow Visible={showUpDownButtons} DataTestId={props.DataTestId} Direction="down" Title="Decrement value" OnClick={decrementValue} />
//             <InputIcon Icon={props.Icon} DataTestId={props.DataTestId} />
//             <input
//                 autoFocus={props.AutoFocus}
//                 autoComplete={getAutocomplete()}
//                 className={getClassName()}
//                 id={props.Id}
//                 maxLength={props.MaxLength}
//                 onBlur={blurHandler}
//                 onChange={changeHandler}
//                 onFocus={focusHandler}
//                 readOnly={props.ReadOnly}
//                 onKeyDown={filterInvalidChars}
//                 placeholder={props.Placeholder}
//                 type="text"
//                 style={props.Style}
//                 title={props.Title || props.Value}
//                 value={props.Value}
//                 data-testid={props.DataTestId} />
//             <InputCopyButton
//                 Show={props.ShowCopyButton}
//                 Icon={getClipboardIcon()}
//                 Copy={copyToClipboard}
//                 DataTestId={props.DataTestId} />
//             <InputErrorIndicator
//                 Error={getErrorMessage(errorMessage)}
//                 DataTestId={props.DataTestId} />
//             <InputNumberArrow Visible={showUpDownButtons} DataTestId={props.DataTestId} Direction="up" Title="Increment value" OnClick={incrementValue} />
//         </div>
//     );
// }


export { InputNumber };
